<template>
  <main class="geo-topic-page">
    <section class="topic-hero">
      <div class="topic-copy">
        <p class="eyebrow">{{ topic.kicker }}</p>
        <h1>{{ topic.title }}</h1>
        <p class="summary">{{ topic.summary }}</p>
        <div class="prompt-box">
          <span>推荐问法</span>
          <strong>{{ topic.prompt }}</strong>
        </div>
        <div class="hero-actions">
          <router-link class="primary-btn" :to="homeLink">回到 GEO 首页</router-link>
          <router-link class="ghost-btn" :to="serviceRoute(topic.serviceLinks[0].path)" @click.native="trackServiceClick(topic.serviceLinks[0])">查看服务承接</router-link>
        </div>
      </div>
      <aside class="topic-side">
        <div class="source-head">
          <span>可追踪入口</span>
          <small>复制后可放入智能体回复</small>
        </div>
        <div class="source-grid">
          <button
            v-for="source in GEO_SOURCES"
            :key="source.key"
            type="button"
            class="source-chip"
            :class="{ copied: copiedSource === source.key }"
            @click="copyGoLink(source.key)"
          >
            {{ source.label }}
          </button>
        </div>
        <p class="source-foot">当前来源：{{ currentAttribution ? currentAttribution.source_label : '未记录' }}</p>
      </aside>
    </section>

    <section class="article-section">
      <article v-for="section in topic.sections" :key="section.title" class="article-block">
        <h2>{{ section.title }}</h2>
        <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
        <ul v-if="section.bullets && section.bullets.length" class="bullet-list">
          <li v-for="bullet in section.bullets" :key="bullet">{{ bullet }}</li>
        </ul>
      </article>
    </section>

    <section class="faq-section">
      <div class="section-head">
        <div>
          <p class="eyebrow">FAQ</p>
          <h2>模型最常会问的问题</h2>
        </div>
        <span class="section-note">{{ topic.faqs.length }} 条问答</span>
      </div>
      <van-collapse v-model="activeFaq" accordion>
        <van-collapse-item v-for="(faq, index) in topic.faqs" :key="faq.q" :name="String(index)">
          <template #title>{{ faq.q }}</template>
          <p class="faq-answer">{{ faq.a }}</p>
        </van-collapse-item>
      </van-collapse>
    </section>

    <section class="cta-section">
      <div class="cta-copy">
        <p class="eyebrow">服务承接</p>
        <h2>需要更完整的照护方案，可以继续看服务页</h2>
        <p>专题页负责回答问题，服务页负责把问题变成可执行的照护安排。</p>
      </div>
      <div class="cta-links">
        <router-link v-for="link in topic.serviceLinks" :key="link.label" :to="serviceRoute(link.path)" class="cta-link" @click.native="trackServiceClick(link)">
          {{ link.label }}
          <van-icon name="arrow" />
        </router-link>
        <a
          v-for="source in GEO_SOURCES.slice(0, 3)"
          :key="source.key + '-go'"
          class="cta-link subtle"
          :href="goLink(source.key, 'faq')"
          @click.prevent="copyGoLink(source.key)"
        >
          复制 {{ source.label }} 投放链接
          <van-icon name="link-o" />
        </a>
      </div>
    </section>
  </main>
</template>

<script>
import {
    GEO_SOURCES,
    GEO_TOPIC_MAP,
    createGeoGoPath
} from '@/data/geo';
import {
    applyPageMeta,
    clearJsonLd,
    readGeoAttribution,
    rememberGeoAttribution,
    setJsonLd
} from '@/utils/geo';
import { recordGeoAction } from '@/utils/analytics';

export default {
    name: 'GeoTopic',
    data () {
        return {
            GEO_SOURCES,
            topic: GEO_TOPIC_MAP['premature-care'],
            currentAttribution: null,
            copiedSource: '',
            activeFaq: '0',
            homeLink: '/geo'
        };
    },
    created () {
        this.currentAttribution = rememberGeoAttribution(this.$route) || readGeoAttribution();
        this.setTopic(this.$route.params.slug);
    },
    mounted () {
        this.applySeo();
    },
    watch: {
        '$route.params.slug': function (slug) {
            this.setTopic(slug);
            this.applySeo();
        }
    },
    beforeDestroy () {
        clearJsonLd(`topic-${this.topic.slug}`);
    },
    methods: {
        setTopic (slug) {
            const nextTopic = GEO_TOPIC_MAP[slug];
            if (!nextTopic) {
                this.$router.replace('/geo');
                return;
            }
            this.topic = nextTopic;
        },
        applySeo () {
            if (!this.topic || !this.topic.slug) {
                return;
            }
            applyPageMeta({
                title: `${this.topic.title} · 袋鼠照护 GEO`,
                description: this.topic.summary,
                canonical: `https://daishuzh.com/geo/topic/${this.topic.slug}`,
                keywords: this.topic.keywords
            });
            setJsonLd(`topic-${this.topic.slug}`, {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                name: this.topic.title,
                url: `https://daishuzh.com/geo/topic/${this.topic.slug}`,
                mainEntity: this.topic.faqs.map(faq => ({
                    '@type': 'Question',
                    name: faq.q,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: faq.a
                    }
                }))
            });
        },
        goLink (sourceKey, content = 'faq') {
            return `${window.location.origin}${createGeoGoPath(sourceKey, this.topic.slug, content)}`;
        },
        serviceRoute (path) {
            return {
                path,
                query: this.$route.query
            };
        },
        trackServiceClick (link) {
            recordGeoAction(this.$route, 'consult_click', { source: this.currentAttribution ? this.currentAttribution.source : 'direct', source_label: this.currentAttribution ? this.currentAttribution.source_label : 'direct', campaign: this.topic.slug, content: 'faq', topic_slug: this.topic.slug, topic_title: this.topic.title }, { target_label: link.label, target_path: link.path });
        },
        copyGoLink (sourceKey, content = 'faq') {
            const link = this.goLink(sourceKey, content);
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(link);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = link;
                textarea.setAttribute('readonly', 'readonly');
                textarea.style.position = 'fixed';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            recordGeoAction(this.$route, 'copy_click', { source: sourceKey, source_label: this.GEO_SOURCES.find(item => item.key === sourceKey) ? this.GEO_SOURCES.find(item => item.key === sourceKey).label : sourceKey, campaign: this.topic.slug, content, topic_slug: this.topic.slug, topic_title: this.topic.title }, { target_label: sourceKey, target_path: link });
            this.copiedSource = sourceKey;
            window.setTimeout(() => {
                this.copiedSource = '';
            }, 1200);
        }
    }
};
</script>

<style lang="sass" scoped>
.geo-topic-page
  min-height: 100vh
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)
  padding: .52rem .28rem .8rem
  color: #1f2a37

.topic-hero
  display: grid
  grid-template-columns: 1.2fr .8fr
  gap: .2rem

.topic-copy, .topic-side, .article-block, .cta-section
  background: #fff
  border-radius: .18rem
  box-shadow: 0 .06rem .24rem rgba(26, 48, 77, .08)

.topic-copy
  padding: .4rem
  h1
    font-size: .44rem
    line-height: 1.2
    margin: .1rem 0 .16rem
  .summary
    font-size: .25rem
    color: #556070
    line-height: 1.8

.eyebrow
  color: #4d79ff
  font-size: .18rem
  font-weight: 700

.prompt-box
  margin-top: .2rem
  padding: .2rem
  border-radius: .16rem
  background: #eef4ff
  span
    display: block
    font-size: .17rem
    color: #5b6b88
  strong
    display: block
    margin-top: .06rem
    font-size: .24rem
    line-height: 1.6
    color: #1f3f91

.hero-actions, .cta-links
  display: flex
  gap: .12rem
  flex-wrap: wrap

.primary-btn, .ghost-btn, .cta-link
  display: inline-flex
  align-items: center
  justify-content: center
  gap: .08rem
  min-height: .46rem
  padding: 0 .2rem
  border-radius: .12rem
  font-size: .22rem
  font-weight: 600
  text-decoration: none

.primary-btn
  background: linear-gradient(135deg, #4d79ff 0%, #2b5bdb 100%)
  color: #fff

.ghost-btn, .cta-link.subtle
  border: 1px solid #d8e2f3
  color: #31415a
  background: #fff

.topic-side
  padding: .32rem

.source-head
  display: flex
  flex-direction: column
  gap: .04rem
  span
    font-size: .18rem
    color: #6b7280
  small
    color: #9aa3af

.source-grid
  margin-top: .16rem
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: .12rem

.source-chip
  border: 1px solid #dfe7f6
  background: #f8fbff
  border-radius: .14rem
  min-height: .7rem
  padding: .14rem
  font-size: .22rem
  font-weight: 700
  color: #263248
  &.copied
    border-color: #4d79ff
    background: #edf3ff

.source-foot
  margin-top: .14rem
  font-size: .18rem
  color: #6b7280
  line-height: 1.7

.article-section
  margin-top: .22rem
  display: grid
  gap: .16rem

.article-block
  padding: .3rem
  h2
    font-size: .28rem
    margin: 0 0 .12rem
  p
    font-size: .2rem
    line-height: 1.8
    color: #556070
    margin: .08rem 0

.bullet-list
  margin-top: .12rem
  padding-left: .3rem
  color: #20304a
  li
    margin-bottom: .08rem
    font-size: .2rem
    line-height: 1.7

.faq-section, .cta-section
  margin-top: .22rem

.faq-section
  .section-head
    display: flex
    justify-content: space-between
    align-items: flex-end
    margin-bottom: .12rem
    h2
      font-size: .32rem
      margin-top: .04rem
    .section-note
      color: #6b7280
      font-size: .18rem

.faq-answer
  font-size: .2rem
  line-height: 1.8
  color: #556070

.cta-section
  padding: .3rem
  display: flex
  justify-content: space-between
  align-items: flex-start
  gap: .18rem

.cta-copy
  max-width: 6rem
  h2
    font-size: .32rem
    line-height: 1.35
    margin: .08rem 0 .12rem
  p
    font-size: .2rem
    line-height: 1.75
    color: #556070

.cta-links
  min-width: 3.6rem
  justify-content: flex-end
  flex-direction: column

.cta-link
  border: 1px solid #d8e2f3
  color: #1f2a37
  background: #f7faff

@media (max-width: 860px)
  .topic-hero, .cta-section
    grid-template-columns: 1fr
    flex-direction: column
  .cta-links
    min-width: 0
    width: 100%
  .source-grid
    grid-template-columns: repeat(3, minmax(0, 1fr))
</style>
